import React, { useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useRouter } from 'next/router';

import Auth from 'services/auth';
import crossTabTokenRefreshService from 'services/crossTabTokenRefreshServiceSingleton';
import { AuthContext } from './AuthContext';
import LoadingPage from '../../../components/layout/Page/LoadingPage';
import { useNotification } from '../hooks';
import CookieUtils from 'utils/cookies';
import routes from 'routes';
import { AUTHENTICATION_ACTIONS, telemetry } from 'utils/telemetry';

/**
 * DigletAuthContextProvider manages user authentication state for the legacy Diglet sign-in.
 * It fetches user identifiers on mount, handles loading states, and provides methods for
 * initializing user data on sign-in and logging out.
 *
 * Re-routing needs should be handled in the layout components.
 *
 * @param {React.ReactNode} children - The child components to be wrapped by the provider.
 * @returns {JSX.Element} The AuthContext.Provider component with user state and methods.
 */
export const DigletAuthContextProvider = ({ children }) => {
  const router = useRouter();
  const notify = useNotification();
  const timerId = useRef<NodeJS.Timer | null>(null);

  const [userLoading, setUserLoading] = useState(true);
  const [user, setUser] = useState(undefined);
  const apollo = useApolloClient();

  const getIdentifiers = async () => {
    setUserLoading(true);

    try {
      /**
       * Check for valid user identifiers in cookies, regardless of the current route. Re-route as
       * needed in the layout components. The only exception is the logout route, on which we do
       * not need to know who the user is, as we are logging them out.
       */
      if (CookieUtils.canGetUserInfo() && router.asPath !== routes.Logout.as) {
        const me = await Auth.getMe();
        setUser({ ...me?.data, scopes: CookieUtils.getUserScopes() });
      }

      setUserLoading(false);
    } catch (e) {
      telemetry.addError(e);
      notify({
        status: 'error',
        description: 'Could not fetch user data. Please login again after we log you out.',
      });

      timerId.current = setTimeout(() => {
        telemetry.addAction(AUTHENTICATION_ACTIONS.AUTH_CONTEXT_REDIRECT_TO_LOGOUT);

        router.replace(routes.Logout.as).then(() => {
          setUserLoading(false);
        });
      }, 5000);
    }
  };

  useEffect(() => {
    telemetry.addAction(AUTHENTICATION_ACTIONS.SESSION_BEGIN);
    getIdentifiers();

    return () => {
      if (timerId?.current) {
        clearTimeout(timerId?.current as NodeJS.Timeout);
      }
    };
  }, []);

  const onSignInInitializeUser = (user) => {
    return setUser(user);
  };

  const onLogout = () => {
    apollo.clearStore();
    setUser(undefined);
    crossTabTokenRefreshService.destroy();
  };

  if (userLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <AuthContext.Provider
        value={{ user, loading: userLoading, isAuth0Login: false, onSignInInitializeUser, onLogout }}
      >
        {children}
      </AuthContext.Provider>
    </>
  );
};
